/**
 * Layout — global chrome wrapper. Adds:
 *   - Top BETA / open-playtest banner with feedback link (matches the
 *     fan-made-playtest framing the competing wondersccg.online client
 *     uses; sets expectations for testers and provides a single feedback
 *     channel)
 *   - Persistent footer: build hash + timestamp + IP disclaimer + brand
 *     cluster
 *
 * Used as the outermost wrapper for everything *except* the in-match
 * Table view (which goes full-bleed). The BETA banner is dismissable
 * per-session via localStorage.
 */

import { type ReactElement, useState } from 'react';

const FEEDBACK_URL = 'https://github.com/salishforge/wonders-ccg-online/issues/new';
const DISCORD_URL = 'https://discord.gg/wondersccg';
const WONDERSCCG_URL = 'https://wondersccg.com';
const DECKPLANET_URL = 'https://deckplanet.app';

const DISMISS_KEY = 'wonders-banner-dismissed';

type Props = {
  children: ReactElement | ReactElement[];
  /** When true, hides the banner + footer (used inside the in-match Table). */
  fullBleed?: boolean;
};

export function Layout({ children, fullBleed }: Props): ReactElement {
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // localStorage disabled — banner reappears next mount, fine.
    }
    setDismissed(true);
  };

  if (fullBleed) {
    return <>{children}</>;
  }

  return (
    <div className="flex min-h-screen flex-col bg-cosmos-950 text-cosmos-100">
      {!dismissed && (
        <div
          role="status"
          className="border-amber-700/60 border-b bg-amber-900/30 px-4 py-2 text-center text-sm text-amber-200"
        >
          <span className="font-medium">Open playtest.</span> This is an unofficial fan-made tool.
          Please report bugs and feedback so we can stabilise the beta.{' '}
          <a
            href={FEEDBACK_URL}
            target="_blank"
            rel="noreferrer"
            className="underline hover:text-amber-100"
          >
            Feedback
          </a>{' '}
          ·{' '}
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noreferrer"
            className="underline hover:text-amber-100"
          >
            Discord
          </a>
          <button
            type="button"
            onClick={dismiss}
            className="ml-3 rounded border border-amber-700/60 px-2 py-0.5 text-xs hover:bg-amber-800/40"
            aria-label="Dismiss banner"
          >
            ×
          </button>
        </div>
      )}

      <main className="flex-1">{children}</main>

      <footer className="border-cosmos-800 border-t bg-cosmos-900/50 px-4 py-3 text-xs text-cosmos-400">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-2">
          <div>
            <span className="font-mono">build {__WONDERS_BUILD_HASH__}</span>{' '}
            <span className="text-cosmos-500">·</span>{' '}
            <span className="text-cosmos-500">{__WONDERS_BUILD_TIME__}</span>
          </div>
          <div className="flex gap-3">
            <a
              href={WONDERSCCG_URL}
              target="_blank"
              rel="noreferrer"
              className="hover:text-cosmos-200"
            >
              WondersCCG ↗
            </a>
            <a
              href={DECKPLANET_URL}
              target="_blank"
              rel="noreferrer"
              className="hover:text-cosmos-200"
            >
              Deck Planet ↗
            </a>
          </div>
        </div>
        <p className="mx-auto mt-2 max-w-5xl text-cosmos-500 text-[11px] leading-tight">
          Unofficial fan-made playtesting tool. Not affiliated with or endorsed by Wonders CCG. All
          card art, names, and game design belong to wondersccg.com. Non-commercial playtesting use
          only.
        </p>
      </footer>
    </div>
  );
}
